import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DocumentExtractionCategory } from '../../enums/document-extraction-category.enum';
import {
    DocumentExtractionState,
    QueuedDocument,
} from './document-extraction.state';
import { validateDocumentForExtraction } from './document-extraction-validation';
import { GetDocumentExtractionCategories } from './thunks/get-document-extraction-categories.thunk';
import { UploadDocumentForExtraction } from './thunks/upload-document-for-extraction.thunk';

const initialState: DocumentExtractionState = {
    categories: [],
    categoriesStatus: 'idle',
    queue: [],
    isSending: false,
};

const createLocalId = (index: number): string =>
    `${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`;

interface EnqueueDocumentsPayload {
    category: DocumentExtractionCategory;
    files: File[];
    rejected?: { file: File; reason: string }[];
}

const enqueueDocumentsPrepare = (payload: EnqueueDocumentsPayload) => {
    const acceptedItems: QueuedDocument[] = payload.files.map((file, index) => {
        const validationError = validateDocumentForExtraction(file);
        return {
            localId: createLocalId(index),
            category: payload.category,
            file,
            status: validationError ? 'failed' : 'queued',
            error: validationError ?? undefined,
            isRetryable: false,
        };
    });

    const rejectedItems: QueuedDocument[] = (payload.rejected ?? []).map(
        (rejection, index) => ({
            localId: createLocalId(payload.files.length + index),
            category: payload.category,
            file: rejection.file,
            status: 'failed',
            error: rejection.reason,
            isRetryable: false,
        })
    );

    return { payload: { items: [...acceptedItems, ...rejectedItems] } };
};

const slice = createSlice({
    name: 'documentExtraction',
    initialState,
    reducers: {
        enqueueDocuments: {
            reducer: (state, action: PayloadAction<{ items: QueuedDocument[] }>) => {
                state.queue.push(...action.payload.items);
            },
            prepare: enqueueDocumentsPrepare,
        },
        removeQueuedDocument: (state, action: PayloadAction<string>) => {
            state.queue = state.queue.filter(
                (item) => item.localId !== action.payload
            );
        },
        setSending: (state, action: PayloadAction<boolean>) => {
            state.isSending = action.payload;
        },
        clearQueue: (state) => {
            state.queue = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(GetDocumentExtractionCategories.pending, (state) => {
                state.categoriesStatus = 'loading';
            })
            .addCase(GetDocumentExtractionCategories.fulfilled, (state, action) => {
                state.categoriesStatus = 'succeeded';
                state.categories = action.payload;
            })
            .addCase(GetDocumentExtractionCategories.rejected, (state) => {
                state.categoriesStatus = 'failed';
            })
            .addCase(UploadDocumentForExtraction.pending, (state, action) => {
                const item = state.queue.find(
                    (queuedItem) => queuedItem.localId === action.meta.arg.localId
                );
                if (item) {
                    item.status = 'sending';
                    item.error = undefined;
                }
            })
            .addCase(UploadDocumentForExtraction.fulfilled, (state, action) => {
                state.queue = state.queue.filter(
                    (item) => item.localId !== action.meta.arg.localId
                );
            })
            .addCase(UploadDocumentForExtraction.rejected, (state, action) => {
                const item = state.queue.find(
                    (queuedItem) => queuedItem.localId === action.meta.arg.localId
                );
                if (item) {
                    item.status = 'failed';
                    item.error =
                        action.payload?.reason ?? 'Upload failed. Please try again.';
                    item.isRetryable = true;
                }
            });
    },
});

export const {
    enqueueDocuments,
    removeQueuedDocument,
    setSending,
    clearQueue,
} = slice.actions;

export default slice.reducer;
